import React from "react";
import {
  IRegistrationForm,
  ITpaSelfInsured,
  ITpaSelfInsuredQuestion,
} from "../data";
import { Tooltip } from "react-tooltip";
import { YES, NO, YES_OR_NO } from "../data";
import { FormGroup } from "./layout";
import { Dispatch } from "./registration_form_component";
import { formatCoveredLives } from "./helpers";

const TPA_SELF_INSURED_QUESTIONS: Record<
  keyof ITpaSelfInsured,
  { question: string; numberOfGroupsTooltip: string }
> = {
  elected: {
    question:
      "1. For the previous plan year, did any of your self-insured clients elect to have you, the third-party administrator, submit encounter data and contracted price data on their behalf?",
    numberOfGroupsTooltip:
      "Please indicate the number of self-insured groups that have elected to have you, the TPA, submit their encounter and contracted price data to the APCD on their behalf.",
  },
  declined: {
    question:
      "2. For the previous plan year, did any of your self-insured clients decline to have you, the third-party administrator, submit encounter data and contracted price data on their behalf?",
    numberOfGroupsTooltip:
      "Please indicate the number of self-insured groups that have declined to have you, the TPA, submit their encounter and contracted price data to the APCD on their behalf.",
  },
  no_response: {
    question:
      "3. For the previous plan year, did any of your self-insured clients not respond to your request regarding submission of encounter data and contracted price data?",
    numberOfGroupsTooltip:
      "Please indicate the number of self-insured groups that have not responded to your request regarding submission of encounter and contracted price data.",
  },
};

export interface ITpaSelfInsuredFormProps {
  form: IRegistrationForm | null;
  dispatch: Dispatch;
}
export const TpaSelfInsuredForm: React.FC<ITpaSelfInsuredFormProps> = ({
  form,
  dispatch,
}) => {
  return (
    <>
      {(
        Object.keys(TPA_SELF_INSURED_QUESTIONS) as Array<keyof ITpaSelfInsured>
      ).map((responseType) => {
        const { question, numberOfGroupsTooltip } =
          TPA_SELF_INSURED_QUESTIONS[responseType];
        return (
          <React.Fragment key={`tpa-self-insured-${responseType}`}>
            <hr style={{ padding: "0", marginBottom: "10px" }} />
            <TpaSelfInsuredQuestion
              responseType={responseType}
              question={question}
              numberOfGroupsTooltip={numberOfGroupsTooltip}
              dispatch={dispatch}
              form={form}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

interface ITpaSelfInsuredQuestionProps {
  responseType: keyof ITpaSelfInsured;
  question: string;
  numberOfGroupsTooltip: string;
  dispatch: Dispatch;
  form: IRegistrationForm | null;
}

const TpaSelfInsuredQuestion: React.FC<ITpaSelfInsuredQuestionProps> = ({
  responseType,
  question,
  numberOfGroupsTooltip,
  dispatch,
  form,
}) => {
  const onRadioButtonChange = (value: YES_OR_NO) => {
    dispatch({
      action: "UPDATE_FORM",
      form: {
        tpa_self_insured: {
          ...form?.tpa_self_insured,
          [responseType]: {
            ...form?.tpa_self_insured?.[responseType],
            any_clients: value,
          },
        },
      },
    });
  };

  return (
    <>
      <p
        id={`tpa-self-insured-${responseType}-question`}
        className="col-sm-12 required"
        style={{ marginBottom: "5px", padding: "0" }}
      >
        {question}
      </p>
      <div
        className="form-group required"
        role="radiogroup"
        style={{ marginLeft: "20px" }}
        aria-labelledby={`tpa-self-insured-${responseType}-question`}
      >
        <div className="radio radio-yes col-sm-1">
          <label>
            <input
              type="radio"
              value={YES}
              checked={
                form?.tpa_self_insured?.[responseType]?.any_clients === YES
              }
              name={`tpa-self-insured-${responseType}-any_clients`}
              onChange={() => onRadioButtonChange(YES)}
              required={true}
              data-fv-notempty="true"
              data-fv-ignore-error-message={true}
              data-fv-notempty-message=""
            />
            Yes
          </label>
        </div>
        <div className="radio radio-no col-sm-1">
          <label>
            <input
              type="radio"
              value={NO}
              checked={
                form?.tpa_self_insured?.[responseType]?.any_clients === NO
              }
              name={`tpa-self-insured-${responseType}-any_clients`}
              onChange={() => onRadioButtonChange(NO)}
              required={true}
              data-fv-notempty="true"
              data-fv-ignore-error-message={true}
              data-fv-notempty-message=""
            />
            No
          </label>
        </div>
      </div>

      {form?.tpa_self_insured?.[responseType]?.any_clients === YES && (
        <>
          <p>
            What are the corresponding numbers of covered lives, claims volume,
            and aggregated claim value for those clients?
          </p>

          <TpaSelfInsuredMetricInput
            dispatch={dispatch}
            form={form}
            responseType={responseType}
            metric="number_of_groups"
            label="Number of Groups"
            tooltip={numberOfGroupsTooltip}
          />

          <TpaSelfInsuredMetricInput
            dispatch={dispatch}
            form={form}
            responseType={responseType}
            metric="covered_lives"
            label="Covered Lives"
            suffix="average per month"
          />

          <TpaSelfInsuredMetricInput
            dispatch={dispatch}
            form={form}
            responseType={responseType}
            metric="claims_volume"
            label="Claims Volume"
            suffix="average per month"
          />

          <TpaSelfInsuredMetricInput
            dispatch={dispatch}
            form={form}
            responseType={responseType}
            metric="aggregated_claim_value"
            label="Aggregated Claim Value (USD)"
            placeholder="Aggregated Claim Value"
            suffix="average per month"
          />
        </>
      )}
    </>
  );
};

interface ITpaSelfInsuredMetricInputProps {
  dispatch: Dispatch;
  form: IRegistrationForm | null;
  responseType: keyof ITpaSelfInsured;
  metric: keyof ITpaSelfInsuredQuestion;
  label: string;
  placeholder?: string;
  tooltip?: string;
  suffix?: string;
}

const TpaSelfInsuredMetricInput: React.FC<ITpaSelfInsuredMetricInputProps> = ({
  dispatch,
  form,
  responseType,
  metric,
  label,
  placeholder,
  tooltip,
  suffix,
}) => {
  const onChange = (value: string) => {
    dispatch({
      action: "UPDATE_FORM",
      form: {
        tpa_self_insured: {
          ...form?.tpa_self_insured,
          [responseType]: {
            ...form?.tpa_self_insured?.[responseType],
            [metric]: value,
          },
        },
      },
    });
  };

  const onBlur = (value: string) => {
    onChange(formatCoveredLives(value));
  };

  return (
    <FormGroup>
      <label
        className="col-sm-4 control-label required"
        htmlFor={`tpa-self-insured-${responseType}-${metric}`}
      >
        {label}
        {tooltip && (
          <>
            &nbsp;
            <i
              className="fa fa-question-circle"
              data-tooltip-id={`tip-tpa-self-insured-${responseType}-${metric}`}
              data-tooltip-content={tooltip}
            />
            <Tooltip
              id={`tip-tpa-self-insured-${responseType}-${metric}`}
              className="registration-tooltip"
            />
          </>
        )}
      </label>
      <div className="col-sm-4">
        <input
          type="text"
          id={`tpa-self-insured-${responseType}-${metric}`}
          className="form-control"
          required={true}
          placeholder={placeholder ?? label}
          value={form?.tpa_self_insured?.[responseType]?.[metric] || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlur(e.target.value)}
          maxLength={20}
          aria-label={label}
        />
      </div>
      {suffix && <p style={{ paddingTop: "7px" }}>{suffix}</p>}
    </FormGroup>
  );
};
